import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getSRStats, getNextQuestion, submitAnswer, getHint, startSession, endSession, SRStats, Question, SubmissionResult } from '../services/api';
import { AIHelpPanel } from '../components/AIHelpPanel';
import { MathRenderer } from '../components/shared/MathRenderer';
import { FeedbackWidget } from '../components/practice/FeedbackWidget';
import { storage } from '../services/storage';

// Format review date relative to today
const formatReviewDate = (dateStr: string): string => {
  const reviewDate = new Date(dateStr + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffDays = Math.round((reviewDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays < 7) return `In ${diffDays} days`;
  return reviewDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const ReviewPage: React.FC = () => {
  const studentId = storage.getStudentId();
  const [stats, setStats] = useState<SRStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(false);
  const [fetchingQuestion, setFetchingQuestion] = useState(false);
  const [question, setQuestion] = useState<Question | null>(null);
  const [answer, setAnswer] = useState('');
  const [result, setResult] = useState<SubmissionResult | null>(null);
  const [sessionStats, setSessionStats] = useState({ reviewed: 0, correct: 0 });
  const [startTime, setStartTime] = useState(Date.now());
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [hintUsed, setHintUsed] = useState(false);
  const [attemptNumber, setAttemptNumber] = useState(1);
  const [firstAttemptAnswer, setFirstAttemptAnswer] = useState('');

  const sessionStatsRef = useRef(sessionStats);
  sessionStatsRef.current = sessionStats;
  const inputRef = useRef<HTMLInputElement>(null);
  const submittingRef = useRef(false);

  const fetchStats = async () => {
    try {
      const data = await getSRStats(studentId);
      setStats(data);
    } catch (err) {
      console.error('Failed to load SR stats:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [studentId]);

  const startReview = async () => {
    // Start a review session
    try {
      const session = await startSession(studentId, 'review');
      setSessionId(session.id);
    } catch (err) {
      console.error('Failed to start review session:', err);
    }
    setReviewing(true);
    setSessionStats({ reviewed: 0, correct: 0 });
    await fetchNextReview();
  };

  const fetchNextReview = async () => {
    setResult(null);
    setAnswer('');
    setHint(null);
    setHintUsed(false);
    setAttemptNumber(1);
    setFirstAttemptAnswer('');
    setFetchingQuestion(true);
    try {
      const q = await getNextQuestion(studentId, 'review_only');
      setQuestion(q);
      setStartTime(Date.now());
    } catch (err: any) {
      // No more reviews
      setQuestion(null);
    } finally {
      setFetchingQuestion(false);
    }
  };

  const handleSubmit = async () => {
    if (!question || !answer.trim()) return;
    if (submittingRef.current) return;
    submittingRef.current = true;

    const timeMs = Date.now() - startTime;

    try {
      const res = await submitAnswer(studentId, question.question_id, answer, timeMs, hintUsed, attemptNumber);

      // If backend says allow_retry → enter retry mode, don't count stats yet
      if (res.allow_retry) {
        setResult(res);
        setFirstAttemptAnswer(answer);
        return;
      }

      setResult(res);
      setSessionStats(prev => ({
        reviewed: prev.reviewed + 1,
        correct: prev.correct + (res.correct ? 1 : 0)
      }));
    } catch (err) {
      console.error('Failed to submit:', err);
    } finally {
      submittingRef.current = false;
    }
  };

  const handleRetry = () => {
    setAnswer('');
    setAttemptNumber(2);
    setResult(null);
    setStartTime(Date.now());
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleShowAnswer = async () => {
    if (!question) return;
    if (submittingRef.current) return;
    submittingRef.current = true;

    const timeMs = Date.now() - startTime;
    try {
      const res = await submitAnswer(
        studentId,
        question.question_id,
        firstAttemptAnswer || answer,
        timeMs,
        hintUsed,
        2
      );
      setResult(res);
      setSessionStats(prev => ({
        reviewed: prev.reviewed + 1,
        correct: prev.correct + (res.correct ? 1 : 0)
      }));
    } catch (err) {
      console.error('Failed to submit:', err);
    } finally {
      submittingRef.current = false;
    }
  };

  const handleHint = async () => {
    if (!question) return;
    try {
      const hintText = await getHint(question.question_id, studentId);
      setHint(hintText);
      setHintUsed(true);
    } catch (err) {
      console.error('Failed to get hint:', err);
    }
  };

  const handleNext = async () => {
    await fetchNextReview();
    // Refresh stats after each review
    fetchStats();
  };

  const finishReviewSession = async () => {
    // End the session in the database
    if (sessionId && sessionStatsRef.current.reviewed > 0) {
      try {
        await endSession(sessionId, sessionStatsRef.current.reviewed, sessionStatsRef.current.correct);
      } catch (err) {
        console.error('Failed to end session:', err);
      }
    }
    setSessionId(null);
    setReviewing(false);
    setQuestion(null);
    setResult(null);
    fetchStats();
  };

  // Handle keyboard navigation
  useEffect(() => {
    if (!reviewing) return;

    const handleKeyDown = async (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isTyping = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
      const isInAIChat = target.closest('.ai-help-panel') !== null;

      if (isInAIChat) return;

      if (e.key === 'Enter') {
        // Enter during retry mode → trigger retry
        if (result?.allow_retry && !isTyping) {
          e.preventDefault();
          handleRetry();
          return;
        }
        // Enter when final result shown → next question
        if (result && !result.allow_retry && !isTyping) {
          e.preventDefault();
          await fetchNextReview();
          fetchStats();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [reviewing, result]);

  // Auto-focus input when new question loads or after retry
  useEffect(() => {
    if (question && !result) {
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [question, result]);

  if (loading) {
    return <div className="loading">Loading review stats...</div>;
  }

  // Review session in progress
  if (reviewing) {
    // Show loading while fetching first/next question
    if (fetchingQuestion) {
      return (
        <div className="review-page">
          <div className="loading">Loading next review...</div>
        </div>
      );
    }

    if (!question) {
      // Session complete
      const accuracy = sessionStats.reviewed > 0
        ? Math.round((sessionStats.correct / sessionStats.reviewed) * 100)
        : 0;

      return (
        <div className="review-page">
          <div className="session-complete">
            <div className="complete-icon">
              {accuracy >= 80 ? '🎉' : accuracy >= 60 ? '👍' : '💪'}
            </div>
            <h2>Review Session Complete!</h2>
            <div className="session-summary">
              <div className="stat-box">
                <span className="stat-value">{sessionStats.reviewed}</span>
                <span className="stat-label">Reviewed</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{sessionStats.correct}</span>
                <span className="stat-label">Correct</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{accuracy}%</span>
                <span className="stat-label">Accuracy</span>
              </div>
            </div>
            <div className="complete-actions">
              <button className="primary-btn" onClick={finishReviewSession}>
                Done
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="review-page">
        <div className="practice-header">
          <h2>Review Session</h2>
          <div className="session-stats">
            {sessionStats.reviewed} reviewed | {sessionStats.correct} correct
            <button className="secondary-btn" onClick={finishReviewSession} style={{ marginLeft: '1rem' }}>
              End Session
            </button>
          </div>
        </div>

        <div className="question-card">
          <div className="question-header">
            <div className="question-header-left">
              <span className="question-id">{question.question_id}</span>
              <span className="review-badge">Review</span>
            </div>
          </div>

          <div className="question-prompt">
            <MathRenderer content={question.prompt} />
          </div>

          <div className="answer-section">
            {question.answer_type === 'multiple_choice' && question.choices && question.choices.length > 0 ? (
              <div className="mc-choices">
                {question.choices.map((choice) => (
                  <button
                    key={choice.value}
                    className={`mc-choice-btn ${answer === choice.value ? 'selected' : ''}`}
                    onClick={() => { setAnswer(choice.value); }}
                    disabled={!!result}
                  >
                    {choice.value}
                  </button>
                ))}
              </div>
            ) : (
              <input
                ref={inputRef}
                type="text"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Enter your answer..."
                disabled={!!result}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && !result && handleSubmit()}
              />
            )}

            {!result && (
              <div className="action-buttons">
                {question.hint_available && !hint && (
                  <button className="hint-btn" onClick={handleHint}>
                    💡 Hint
                  </button>
                )}
                <button className="submit-btn" onClick={handleSubmit}>
                  Check Answer
                </button>
              </div>
            )}
          </div>

          {hint && (
            <div className="hint-box">
              <strong>Hint:</strong> <MathRenderer content={hint} />
            </div>
          )}

          {/* AI Help Panel - Socratic dialogue while working on question */}
          {!result && (
            <AIHelpPanel
              questionId={question.question_id}
              studentId={studentId}
              studentAnswer={answer}
              showAfterResult={false}
              questionPrompt={question.prompt}
            />
          )}

          {/* Retry mode: first wrong attempt, backend allows retry */}
          {result && result.allow_retry && (
            <div className="result-box retry-mode">
              <div className="result-header">
                Not quite — try again!
              </div>

              {result.hint_text && (
                <div className="retry-hint">
                  <strong>Hint:</strong> <MathRenderer content={result.hint_text} />
                </div>
              )}

              <div className="retry-buttons">
                <button className="retry-btn" onClick={handleRetry}>
                  Try Again
                </button>
                <button className="show-answer-btn" onClick={handleShowAnswer}>
                  Show Answer
                </button>
              </div>
              <div className="keyboard-hint">
                Press <span className="kbd">Enter</span> to try again
              </div>
            </div>
          )}

          {/* Final result: correct/incorrect with explanation */}
          {result && !result.allow_retry && (
            <div className={`result-box ${result.correct ? 'correct' : 'incorrect'}`}>
              <div className="result-header">
                {result.correct
                  ? attemptNumber === 2
                    ? 'Got it on the second try!'
                    : '✅ Correct!'
                  : '❌ Incorrect'}
              </div>

              {!result.correct && result.correct_answer && (
                <div className="correct-answer">
                  The answer was: <strong>{result.correct_answer}</strong>
                </div>
              )}

              {result.explanation && (
                <div className="explanation">
                  <MathRenderer content={result.explanation} />
                </div>
              )}

              <div className="sr-feedback">
                {result.rating && (
                  <span className={`rating-badge rating-${result.rating.toLowerCase()}`}>
                    {result.rating}
                  </span>
                )}
                {result.next_review_date && (
                  <span className="next-review">
                    Next review: {formatReviewDate(result.next_review_date)}
                  </span>
                )}
              </div>

              <button className="next-btn" onClick={handleNext}>
                Next Review →
              </button>
            </div>
          )}

          {/* AI Help Panel - explain/worked example after final result */}
          {result && !result.allow_retry && (
            <AIHelpPanel
              questionId={question.question_id}
              studentId={studentId}
              studentAnswer={answer}
              showAfterResult={true}
              questionPrompt={question.prompt}
              correctAnswer={result.correct_answer || undefined}
            />
          )}

          {/* Feedback Widget - shows after incorrect answers */}
          {result && !result.allow_retry && !result.correct && (
            <FeedbackWidget
              questionId={question.question_id}
              studentId={studentId}
            />
          )}
        </div>
      </div>
    );
  }

  // Stats overview
  return (
    <div className="review-page">
      <h1>Spaced Repetition</h1>
      <p className="review-subtitle">
        Review cards at optimal intervals to maximize retention
      </p>

      {stats && (
        <>
          <div className="sr-stats-grid">
            <div className={`sr-stat-card ${stats.due_today > 0 ? 'sr-highlight' : ''}`}>
              <div className="sr-stat-value">{stats.due_today}</div>
              <div className="sr-stat-label">Due Today</div>
            </div>
            <div className="sr-stat-card">
              <div className="sr-stat-value">{stats.due_tomorrow}</div>
              <div className="sr-stat-label">Due Tomorrow</div>
            </div>
            <div className="sr-stat-card">
              <div className="sr-stat-value">{stats.due_this_week}</div>
              <div className="sr-stat-label">This Week</div>
            </div>
            <div className="sr-stat-card">
              <div className="sr-stat-value">{stats.total_cards}</div>
              <div className="sr-stat-label">Total Cards</div>
            </div>
          </div>

          <div className="card-states">
            <div className="card-state">
              <span className="state-count state-new">{stats.new_cards}</span>
              <span className="state-label">New</span>
            </div>
            <div className="card-state">
              <span className="state-count state-learning">{stats.learning_cards}</span>
              <span className="state-label">Learning</span>
            </div>
            <div className="card-state">
              <span className="state-count state-review">{stats.review_cards}</span>
              <span className="state-label">Review</span>
            </div>
            <div className="card-state">
              <span className="state-count state-relearning">{stats.relearning_cards}</span>
              <span className="state-label">Relearning</span>
            </div>
          </div>

          {stats.retention_rate_30d !== null && (
            <div className="retention-display">
              <div className="retention-value">
                {Math.round(stats.retention_rate_30d * 100)}%
              </div>
              <div className="retention-label">30-Day Retention Rate</div>
            </div>
          )}

          <div className="review-actions">
            {stats.due_today > 0 ? (
              <button className="review-action-btn primary" onClick={startReview}>
                <div className="action-info">
                  <span className="action-title">Start Review Session</span>
                  <span className="action-desc">{stats.due_today} cards due today</span>
                </div>
                <span className="action-arrow">→</span>
              </button>
            ) : (
              <div className="review-action-btn" style={{ cursor: 'default', opacity: 0.6 }}>
                <div className="action-info">
                  <span className="action-title">All caught up!</span>
                  <span className="action-desc">No reviews due today</span>
                </div>
                <span className="action-arrow">✓</span>
              </div>
            )}

            <Link to="/practice" className="review-action-btn">
              <div className="action-info">
                <span className="action-title">Practice New Material</span>
                <span className="action-desc">{stats.unseen_questions} questions available</span>
              </div>
              <span className="action-arrow">→</span>
            </Link>
          </div>
        </>
      )}

      <Link to="/" className="back-link">← Back to Dashboard</Link>
    </div>
  );
};

export default ReviewPage;
